import {
  DESIGN_SYSTEMS,
  type EditorSDK,
} from '@wix/wix-data-client-common-standalone'
import { EDITOR_TYPE, type AppApi } from '@wix/wix-data-client-common'
import type Experiments from '@wix/wix-experiments'
import { NEW_DYNAMIC_PAGE_FLOWS } from '../../business-logic/dynamic-pages/constants'
import getPanelUrl from '@wix/wix-data-client-common/dist/esm/panels/getPanelUrl'
import { newDynamicPagePanelV5 as newDynamicPagePanelV5Def } from '@wix/wix-data-client-common/dist/esm/panels/panelDefs'
interface PanelOpenerProps {
  hostname: string
  editorSdkProxy: EditorSDK
  editorType: EDITOR_TYPE
  appApi: AppApi
  collectionId?: string
  isFirstTimeExperience: boolean
  origin: string
  canChangeCollection: boolean
  experimentsManager: Experiments
}

const getPanelDimensions = ({
  canChangeCollection,
  isDynamicEditPageAvailable,
  isSufficientScreenHeight,
  isAddDPModalStepsEnabled,
}: {
  canChangeCollection: boolean
  isDynamicEditPageAvailable: boolean
  isSufficientScreenHeight: boolean
  isAddDPModalStepsEnabled: boolean
}) => {
  if (isAddDPModalStepsEnabled) {
    return { width: 748, height: 636 }
  }

  const shouldOpenNewDynamicPageFromCMSPanel = !canChangeCollection

  return shouldOpenNewDynamicPageFromCMSPanel
    ? { width: 628, height: 636 }
    : {
        width: 576,
        height:
          isDynamicEditPageAvailable && isSufficientScreenHeight ? 700 : 600,
      }
}

export const openPanel = async ({
  hostname,
  editorSdkProxy,
  editorType,
  appApi,
  collectionId: preselectedCollectionId,
  isFirstTimeExperience,
  origin,
  canChangeCollection,
  experimentsManager,
}: PanelOpenerProps) => {
  const isDynamicEditPageAvailable = editorType === EDITOR_TYPE.STUDIO

  const isSufficientScreenHeight =
    isDynamicEditPageAvailable &&
    editorSdkProxy.document.environment !== undefined
      ? // `environment` is missing in `document` in the fake editor
        // implementation; although, `environment` is available at the top
        // level, the fake implementation misses `screen` there
        (await editorSdkProxy.document.environment.screen.getScreenResolution())
          .height >= 967
      : true

  const isAddDPModalStepsEnabled = experimentsManager.enabled(
    'specs.wixDataClient.AddDPModalStepsAndMigrateToWDS',
  )

  const { width, height } = getPanelDimensions({
    canChangeCollection,
    isDynamicEditPageAvailable,
    isSufficientScreenHeight,
    isAddDPModalStepsEnabled,
  })

  const panelResult = await editorSdkProxy.editor.openModalPanel({
    url: await getPanelUrl({
      hostname,
      relativeUrl: newDynamicPagePanelV5Def.url,
      editorSdkProxy,
      params: isAddDPModalStepsEnabled
        ? { designSystemsToLoad: [DESIGN_SYSTEMS.WDS] }
        : undefined,
    }),
    width,
    height,
    shouldHideHeader: true,
    initialData: {
      origin,
      editorType,
      isFirstTimeExperience,
      preselectedCollectionId,
      canChangeCollection,
      isDynamicEditPageAvailable,
      isSufficientScreenHeight,
    },
  })

  if (!panelResult) {
    return
  }

  const { type, payload } = panelResult
  const {
    pagesToAdd,
    collectionId,
    collectionName,
    presetId,
    fromExistingCollection,
    panelName,
  } = payload

  switch (type) {
    case NEW_DYNAMIC_PAGE_FLOWS.OPEN_PRESETS_MODAL:
      return appApi.openAddDynamicPagePanelV4({
        origin,
      })

    case NEW_DYNAMIC_PAGE_FLOWS.ADD_FROM_COLLECTION:
      return appApi.addDynamicPages({
        pagesToAdd,
        collectionId,
        collectionName,
        biDefaults: { origin },
        presetId,
        fromExistingCollection,
      })

    case NEW_DYNAMIC_PAGE_FLOWS.ADD_BLANK_DYNAMIC_PAGE:
      return appApi.addBlankDynamicPage({
        collectionId,
        origin,
        panelName,
      })

    case NEW_DYNAMIC_PAGE_FLOWS.OPEN_DYNAMIC_EDIT_PAGE_PANEL:
      return appApi.openDynamicEditPagePanel({
        collectionId,
        origin,
        isFirstTimeExperience,
      })

    case NEW_DYNAMIC_PAGE_FLOWS.ADD_CATEGORY_PAGE:
      return appApi.addCategoryPage({
        collectionId,
        origin,
      })
  }

  return
}
